import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  IncidentDetail,
  IncidentEquipos,
  IncidentesEstadisticas,
  IncidentList,
  Severity,
} from "@/api/types";

export interface IncidentFilters {
  status: "open" | "resolved" | "all" | "needs_human";
  severities: Severity[];
  page: number;
  perPage: number;
}

const DEFAULT_REFETCH_MS = 30_000;

function buildQuery(filters: IncidentFilters): string {
  const params = new URLSearchParams();
  params.set("status", filters.status);
  params.set("page", String(filters.page));
  params.set("per_page", String(filters.perPage));
  if (filters.severities.length > 0) {
    params.set("severity", filters.severities.join(","));
  }
  return params.toString();
}

export function useIncidents(filters: IncidentFilters) {
  return useQuery({
    queryKey: ["incidents", filters],
    queryFn: ({ signal }) =>
      api<IncidentList>(`/api/v1/incidents?${buildQuery(filters)}`, { signal }),
    refetchInterval: DEFAULT_REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

export function useIncident(incidentId: string | undefined) {
  return useQuery({
    queryKey: ["incident", incidentId],
    queryFn: ({ signal }) =>
      api<IncidentDetail>(`/api/v1/incidents/${incidentId}`, { signal }),
    enabled: !!incidentId,
  });
}

export function useIncidentesEstadisticas() {
  return useQuery({
    queryKey: ["incidentes-estadisticas"],
    queryFn: ({ signal }) =>
      api<IncidentesEstadisticas>("/api/v1/incidents/estadisticas", { signal }),
    refetchInterval: DEFAULT_REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

export function useIncidentEquipos(incidentId: string | undefined) {
  return useQuery({
    queryKey: ["incident-equipos", incidentId],
    queryFn: ({ signal }) =>
      api<IncidentEquipos>(`/api/v1/incidents/${incidentId}/equipos`, { signal }),
    enabled: !!incidentId,
    refetchInterval: DEFAULT_REFETCH_MS,
  });
}
